import { Amplify } from 'aws-amplify';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';

// Amplify/Cognito bootstrap for authentication.
// User profile data is loaded via the REST user API.

let isAmplifyConfigured = false;

export const configureAmplify = () => {
  console.log('Configuring Amplify (Cognito)');
  try {
    if (!process.env.REACT_APP_COGNITO_USER_POOL_ID) {
      return false;
    }

    if (!process.env.REACT_APP_COGNITO_CLIENT_ID) {
      return false;
    }

    // BETA-6: the hosted-UI domain is what makes federated sign-in possible.
    // Absent (federation not configured) → no `loginWith.oauth` block and
    // `isGoogleSignInAvailable()` is false, so the button is not rendered at
    // all. That is the actual fix: the bug was a button that could never work,
    // and a button that silently no-ops is worse than no button.
    const oauthDomain = (process.env.REACT_APP_COGNITO_DOMAIN || '').trim();

    const amplifyConfig = {
      Auth: {
        Cognito: {
          userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
          userPoolClientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
          ...(oauthDomain ? {
            loginWith: {
              oauth: {
                domain: oauthDomain,
                scopes: ['openid', 'email', 'profile'],
                // Must match `oauth_callback_urls` / `oauth_logout_urls` in
                // user-infra EXACTLY — Cognito does no wildcard or
                // trailing-slash matching, and a mismatch shows up as
                // "redirect_mismatch" on Cognito's own error page rather than
                // anywhere in this app.
                redirectSignIn: [oauthRedirectUrl()],
                redirectSignOut: [oauthRedirectUrl()],
                // Authorization code + PKCE. Never `implicit`: that returns
                // tokens in the URL fragment, where they reach browser history
                // and any referrer.
                responseType: 'code' as const,
              },
            },
          } : {}),
        },
      },
    };

    Amplify.configure(amplifyConfig);

    const customStorage = {
      setItem: (key: string, value: string) => {
        localStorage.setItem(key, value);
        return Promise.resolve();
      },
      getItem: (key: string) => {
        const value = localStorage.getItem(key);
        return Promise.resolve(value);
      },
      removeItem: (key: string) => {
        localStorage.removeItem(key);
        return Promise.resolve();
      },
      clear: () => {
        localStorage.clear();
        return Promise.resolve();
      },
    };

    cognitoUserPoolsTokenProvider.setKeyValueStorage(customStorage);

    console.log('✅ Amplify configured with localStorage for token persistence');
    isAmplifyConfigured = true;
    return true;
  } catch (error) {
    console.error('❌ Failed to configure Amplify:', error);
    return false;
  }
};

export const isConfigured = () => isAmplifyConfigured;

/**
 * The exact URL Cognito redirects back to. Derived from the running origin
 * rather than hardcoded so localhost, the beta domain and any preview build all
 * work without a rebuild — but each one still has to be listed in
 * `oauth_callback_urls`, because Cognito compares strings.
 */
export const oauthRedirectUrl = (): string => `${window.location.origin}/`;

/**
 * Whether to offer "Continue with Google" at all.
 *
 * BETA-6: before this, both clients showed the button unconditionally while the
 * user pool had no identity provider configured — clicking it did nothing on
 * web and opened the web app on mobile. The control now appears only when the
 * flow behind it exists.
 */
export const isGoogleSignInAvailable = (): boolean =>
  Boolean((process.env.REACT_APP_COGNITO_DOMAIN || '').trim());
